import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import ScrollToTopButton from '../../components/ScrollToTopButton';
import RequestsToJoinItemList from './RequestsToJoinItemList';
import NoInfoMessage from '../../components/NoInfoMessage';
import LoadingSpinner from '../../components/LoadingSpinner';

// TYPES
import { RequestsToJoinPendingRequestsUserJoinRequest } from '../../api/models';

export interface StaffDataTypes {
	id?: string;
	firstName?: string;
	lastName?: string;
	image?: string;
	organisationRole?: string[];
	timeRequested?: string;
}

interface CurrentDataTypes {
	[key: string]: any[];
}

const RequestsToJoin: React.FC = () => {
	// Check if User is Organisation Admin
	const isOrganisationAdmin = useAppSelector((state) => state.user.isOrganisationAdmin);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Data to filter
	const [requestsToJoinData, setRequestsToJoinData] = React.useState<RequestsToJoinPendingRequestsUserJoinRequest[]>(
		[]
	);

	// data used for ordering the sections.
	const [defaultSectionOrder, setDefaultSectionOrder] = React.useState<string[]>([]);
	const [dynamicSectionOrder, setDynamicSectionOrder] = React.useState<string[]>([]);

	// function to  Get staff data from api call
	const getRequestsToJoinData = async () => {
		setIsLoading(true);

		const response = await apibridge.getRequestsToJoinData();

		response.result = response.result || [];

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			setRequestsToJoinData(response.result);
		}
	};

	// state to store current data
	const [currentData, setCurrentData] = React.useState<CurrentDataTypes>({});

	// filter logic
	const filterData = () => {
		const defaultDataOrder: string[] = ['Staff', 'Visitor', 'Parent', 'ParentVolunteer', 'Student'];
		const dynamicDataOrder: string[] = [];
		let currentData: CurrentDataTypes = {};

		requestsToJoinData.forEach((item) => {
			if (item !== undefined) {
				if (item.profileType !== undefined) {
					if (item.isVolunteerRequest && item.profileType === 'Parent') {
						if (!defaultDataOrder.includes('ParentVolunteer')) {
							defaultDataOrder.push('ParentVolunteer');
						}

						if (!dynamicDataOrder.includes('ParentVolunteer')) {
							dynamicDataOrder.push('ParentVolunteer');
						}

						if (currentData['ParentVolunteer']) {
							currentData['ParentVolunteer'].push(item);
						} else {
							currentData['ParentVolunteer'] = [];
							currentData['ParentVolunteer'].push(item);
						}
					} else {
						if (!defaultDataOrder.includes(item.profileType)) {
							defaultDataOrder.push(item.profileType);
						}

						if (!dynamicDataOrder.includes(item.profileType)) {
							dynamicDataOrder.push(item.profileType);
						}

						if (currentData[item.profileType]) {
							currentData[item.profileType].push(item);
						} else {
							currentData[item.profileType] = [];
							currentData[item.profileType].push(item);
						}
					}
				}
			}
		});

		setCurrentData(currentData);

		if (dynamicSectionOrder.length === 0) {
			setDefaultSectionOrder(defaultDataOrder);
			setDynamicSectionOrder(dynamicDataOrder);
		}
	};

	// Calling data from api and set request to join data from api to requestsToJoinData state
	React.useEffect(() => {
		getRequestsToJoinData();
	}, []);

	// filter data once we get the data from api.
	React.useEffect(() => {
		filterData();
	}, [requestsToJoinData]);

	return (
		<div className="page-general-layout staff-page">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<div className="staff-content">
								{isOrganisationAdmin && requestsToJoinData && requestsToJoinData.length > 0 ? (
									<div className="mbtom-30">
										{defaultSectionOrder.map((role: string, index) => {
											if (dynamicSectionOrder.includes(role)) {
												const peopleList: StaffDataTypes[] = currentData[role] || [];

												return <RequestsToJoinItemList key={index} role={role} peopleList={peopleList} />;
											}
										})}
									</div>
								) : (
									<NoInfoMessage name="requests to join" />
								)}

								<ScrollToTopButton />
							</div>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default RequestsToJoin;
